import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader2, Copy, Check, Download, RefreshCw, Radio } from 'lucide-react';
import { apiFetch } from '../services/api_service';


/**
 * AI Assistant Modal - Reusable component for AI-powered features
 * Used across all persona dashboards for quick win features
 * Supports both standard and streaming (SSE) responses
 */
const AIAssistantModal = ({
  isOpen,
  onClose,
  title,
  icon: Icon = Sparkles,
  iconColor = 'text-purple-600',
  iconBg = 'bg-purple-100',
  children,
  result,
  loading,
  error,
  onRetry,
  onCopy,
  onDownload,
  downloadLabel = 'Download',
  // Streaming props
  streamUrl = null,
  streamParams = {},
  onStreamComplete = null
}) => {
  const [copied, setCopied] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const eventSourceRef = useRef(null);
  const contentRef = useRef(null);

  // Handle streaming responses via SSE
  useEffect(() => {
    if (isOpen && streamUrl && !result && !loading) {
      startStreaming();
    }
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [isOpen, streamUrl]);

  // Auto-scroll to bottom during streaming
  useEffect(() => {
    if (contentRef.current && isStreaming) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamedContent, isStreaming]);

  const startStreaming = async () => {
    setIsStreaming(true);
    setStreamedContent('');
    
    try {
      const token = localStorage.getItem('token');
      const queryString = new URLSearchParams(streamParams).toString();
      const url = queryString ? `${streamUrl}?${queryString}` : streamUrl;
      
      // Use fetch with ReadableStream for better SSE handling with auth
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        }
      });

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.chunk) {
                setStreamedContent(prev => prev + data.chunk);
              }
              if (data.done) {
                setIsStreaming(false);
                if (onStreamComplete) {
                  onStreamComplete(streamedContent);
                }
              }
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              // Ignore parse errors for malformed chunks
            }
          }
        }
      }
      
      setIsStreaming(false);
    } catch (err) {
      console.error('Streaming error:', err);
      setIsStreaming(false);
    }
  };

  if (!isOpen) return null;

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
    } else if (result || streamedContent) {
      const textContent = streamedContent || (typeof result === 'string' ? result : JSON.stringify(result, null, 2));
      navigator.clipboard.writeText(textContent);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Use streamed content if available, otherwise fall back to result
  const displayResult = streamedContent || result;
  const isLoading = loading || isStreaming;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
              <Icon size={20} className={iconColor} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {title}
              </h2>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                {isStreaming ? (
                  <>
                    <Radio size={10} className="text-green-500 animate-pulse" /> Streaming live
                  </>
                ) : (
                  <>
                    <Sparkles size={10} /> Powered by AI
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-5 overflow-y-auto max-h-[60vh]">
          {/* Input Form */}
          {children && !displayResult && !isLoading && (
            <div className="space-y-4">
              {children}
            </div>
          )}

          {/* Loading State (non-streaming) */}
          {loading && !isStreaming && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4 animate-pulse">
                <Sparkles size={28} className="text-white" />
              </div>
              <p className="text-slate-900 font-medium mb-2">AI is analyzing...</p>
              <p className="text-sm text-slate-500">This may take a few seconds</p>
              <div className="mt-4 flex items-center gap-2">
                <Loader2 size={16} className="animate-spin text-purple-600" />
                <span className="text-xs text-slate-400">Processing with Claude AI</span>
              </div>
            </div>
          )}

          {/* Streaming State - Show content as it arrives */}
          {isStreaming && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                <Radio size={16} className="animate-pulse" />
                <span>Generating response...</span>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                    {streamedContent}
                    <span className="inline-block w-2 h-4 bg-purple-500 animate-pulse ml-1" />
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isStreaming && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-800 font-medium mb-2">Error generating content</p>
              <p className="text-sm text-red-600 mb-4">{error}</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  <RefreshCw size={16} />
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Result Display */}
          {displayResult && !isLoading && (
            <div className="space-y-4">
              {/* Success Badge */}
              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                <Check size={16} />
                <span>Generated successfully</span>
              </div>

              {/* Result Content */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                {typeof displayResult === 'string' ? (
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                      {displayResult}
                    </pre>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {displayResult.content && (
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                          {displayResult.content}
                        </pre>
                      </div>
                    )}
                    {displayResult.draft_disclosure && (
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                          {displayResult.draft_disclosure}
                        </pre>
                      </div>
                    )}
                    {displayResult.explanation && (
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                          {displayResult.explanation}
                        </pre>
                      </div>
                    )}
                    {displayResult.summary && (
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                          {displayResult.summary}
                        </pre>
                      </div>
                    )}
                    {displayResult.severity && (
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-sm text-slate-600">Severity:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          displayResult.severity === 'high' ? 'bg-red-100 text-red-700' :
                          displayResult.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {displayResult.severity}
                        </span>
                      </div>
                    )}
                    {displayResult.generated_at && (
                      <p className="text-xs text-slate-400 mt-2">
                        Generated: {new Date(displayResult.generated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {displayResult && !isLoading && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-500">
              AI-generated content. Please review before use.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-white transition-colors text-sm"
              >
                {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm"
                >
                  <Download size={16} />
                  {downloadLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistantModal;
